import React, { useEffect, useState, useRef, memo } from "react";
import Image from "next/image";
import { Mail, Github, Linkedin, ArrowRight, Twitter, Music, Moon, Sun, X } from "lucide-react";
import { useTheme } from "next-themes";
import { m as motion, AnimatePresence, useAnimation, AnimationControls } from "framer-motion";
import { FloatingParticles } from "@/components/ui/floating-particles";
import { ProjectCard } from "@/components/ui/project-card";

// --- Native Compact Components ---

function MinimalGitHubGraph({ initialData }: { initialData: any }) {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [hoveredDay, setHoveredDay] = useState<any>(null);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const years = [2026, 2025, 2024, 2023];

  useEffect(() => {
    // Standardize: If initialData is the rolling last year, and we want specifically the current calendar year (Jan-Dec), 
    // we should fetch it to avoid including previous year's months.
    const fetchYearData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/v1/github?year=${selectedYear}`);
        const result = await response.json();
        setData(result);
      } catch (e) {
        console.error("Failed to fetch year data", e);
      } finally {
        setLoading(false);
      }
    };

    fetchYearData();
  }, [selectedYear]);

  if (!data?.weeks && !loading) return <div className="text-xs text-neutral-500">No data available</div>;
  
  const getColor = (count: number) => {
    if (count === 0) return "bg-neutral-100 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-800/80";
    if (count < 3) return "bg-emerald-400/50 dark:bg-emerald-500/30";
    if (count < 6) return "bg-emerald-500/70 dark:bg-emerald-500/50";
    if (count < 10) return "bg-emerald-600/80 dark:bg-emerald-500/70";
    return "bg-emerald-500";
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-neutral-500 font-medium h-5">
          {loading ? (
            <span className="animate-pulse">Loading contributions...</span>
          ) : hoveredDay ? (
            <motion.span 
              initial={{ opacity: 0, y: 5 }} 
              animate={{ opacity: 1, y: 0 }}
              className="text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1.5"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              {hoveredDay.contributionCount} contributions on {new Date(hoveredDay.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </motion.span>
          ) : (
            <span>{data?.totalContributions || 0} contributions in {selectedYear}</span>
          )}
        </div>
        <div className="flex gap-1.5 shrink-0">
          {years.map((y) => (
            <button
              key={y}
              onClick={() => setSelectedYear(y)}
              className={`text-[11px] px-2.5 py-1 rounded-md transition-all ${
                selectedYear === y
                  ? "bg-neutral-900 dark:bg-white text-white dark:text-black font-bold shadow-sm"
                  : "bg-neutral-100 dark:bg-neutral-800/50 text-neutral-500 hover:text-neutral-900 dark:hover:text-white border border-transparent dark:border-neutral-700/30"
              }`}
            >
              {y}
            </button>
          ))}
        </div>
      </div>
      
      <div className={`flex gap-[3px] overflow-x-auto pb-2 scrollbar-none custom-scrollbar shrink-0 w-full transition-opacity duration-300 ${loading ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
        {data?.weeks?.map((week: any, i: number) => (
          <div key={i} className="flex flex-col gap-[3px] shrink-0">
            {week.contributionDays.map((day: any, j: number) => (
              <div 
                key={j} 
                onMouseEnter={() => setHoveredDay(day)}
                onMouseLeave={() => setHoveredDay(null)}
                className={`w-[11px] h-[11px] sm:w-[13px] sm:h-[13px] rounded-[2px] cursor-help relative group transition-transform hover:scale-125 hover:z-50 ${getColor(day.contributionCount)}`} 
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const AnimatedLane = memo(
  ({ item, index }: { item: any; index: number }) => {
    const pathControls = useAnimation();
    const boxControls = useAnimation();
    const [hasMounted, setHasMounted] = useState(false);
    const isStarted = useRef(false);

    useEffect(() => {
      setHasMounted(true);
      let isMounted = true;

      const safeAnimate = async (
        controls: AnimationControls,
        definition: any
      ) => {
        if (!isMounted) return;
        try {
          await controls.start(definition);
        } catch (e) {}
      };

      const runSequence = async () => {
        if (isStarted.current) return;
        await new Promise((r) => setTimeout(r, 1000));
        if (!isMounted) return;
        isStarted.current = true;

        while (isMounted) {
          if (!isMounted) break;

          await safeAnimate(pathControls, {
            opacity: 1,
            pathOffset: 1,
            transition: { duration: 2.5, ease: "easeInOut" },
          });

          if (!isMounted) break;

          safeAnimate(pathControls, {
            opacity: 0,
            pathOffset: 0,
            transition: { duration: 0 },
          });

          await safeAnimate(boxControls, {
            borderColor: item.hex,
            scale: 1.05,
            boxShadow: `0 0 20px ${item.hex}40`,
            transition: { duration: 0.15 },
          });

          await new Promise((r) => setTimeout(r, 1500));

          if (!isMounted) break;

          await safeAnimate(boxControls, {
            borderColor: "rgba(0,0,0,0.1)",
            scale: 1,
            boxShadow: "none",
            transition: { duration: 1 },
          });

          if (!isMounted) break;
          await new Promise((r) => setTimeout(r, 800));
        }
      };

      runSequence();

      return () => {
        isMounted = false;
        try {
          pathControls.stop();
          boxControls.stop();
        } catch (e) {}
      };
    }, [pathControls, boxControls, index, item.hex]);

    const boxPositions = ["top-0", "top-1/2 -translate-y-1/2", "bottom-0"];
    if (!hasMounted) return null;

    return (
      <>
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none z-0"
          viewBox="0 0 600 240"
          preserveAspectRatio="none"
        >
          <g>
            <path
              d={item.d}
              fill="none"
              stroke="currentColor"
              className="text-neutral-200 dark:text-neutral-800"
              strokeWidth="2"
              strokeDasharray="4 4"
            />
            <motion.path
              d={item.d}
              fill="none"
              stroke={item.hex}
              strokeWidth="4"
              strokeLinecap="round"
              initial={{ pathLength: 0.1, pathOffset: 0, opacity: 0 }}
              animate={pathControls}
            />
          </g>
        </svg>

        <div className={`absolute right-0 w-40 sm:w-48 ${boxPositions[index]}`}>
          <motion.div
            className="bg-white dark:bg-black border border-neutral-200 dark:border-neutral-800 px-3 py-2 rounded-lg shadow-sm text-center relative overflow-hidden z-10"
            animate={boxControls}
            initial={{ borderColor: "rgba(0,0,0,0.1)", scale: 1 }}
          >
            <span className="text-[11px] sm:text-[13px] font-bold tracking-tight text-neutral-800 dark:text-neutral-200">
              {item.text}
            </span>
          </motion.div>
        </div>
      </>
    );
  }
);
AnimatedLane.displayName = "AnimatedLane";

function AnimatedGreeting({ text }: { text: string }) {
  return (
    <div className="font-mono text-[14px] font-bold">
      <motion.span
        animate={{ 
          color: ["rgba(163,163,163,0.4)", "rgba(255,255,255,1)", "rgba(163,163,163,0.4)"] 
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="inline-block"
      >
        {text}
      </motion.span>
    </div>
  );
}

function MinimalSpotify({ initialData }: { initialData: any }) {
  const [data, setData] = useState(initialData);

  useEffect(() => {
    const fetchNowPlaying = async () => {
      try {
        const response = await fetch("/api/v1/status");
        setData(await response.json());
      } catch (e) {}
    };
    const interval = setInterval(fetchNowPlaying, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!data) return null;

  return (
    <a href={data.songUrl || "#"} target="_blank" rel="noreferrer" className="flex items-center gap-4 w-full p-4 rounded-[16px] bg-white dark:bg-[#111111]/30 border border-neutral-200 dark:border-neutral-800/60 hover:bg-neutral-50 dark:hover:bg-[#111111]/60 transition-all group shadow-sm dark:shadow-none">
      <div className="relative w-12 h-12 rounded-md overflow-hidden shrink-0 bg-neutral-200 dark:bg-neutral-800 flex items-center justify-center shadow-sm border border-neutral-200 dark:border-neutral-700/50">
        {data.albumImageUrl ? (
           // eslint-disable-next-line @next/next/no-img-element
          <img src={data.albumImageUrl} alt={data.album || "Album"} className={`w-full h-full object-cover transition-all ${!data.isPlaying && "grayscale opacity-60"}`} />
        ) : (
          <Music size={16} className="text-neutral-500" />
        )}
        {data.isPlaying && (
          <div className="absolute inset-0 bg-emerald-500/10 flex items-center justify-center">
            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-ping" />
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1 min-w-0 text-left">
        <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-[0.1em] mb-0.5 mt-0.5">
           {data.isPlaying ? <span className="text-emerald-600 dark:text-emerald-500 flex items-center gap-1.5">Now Playing <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"/></span> : "Last Played"}
        </span>
        <h4 className="text-[14px] font-bold text-neutral-800 dark:text-neutral-200 truncate leading-tight group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">{data.title || "Not Playing"}</h4>
        <p className="text-[12px] text-neutral-500 truncate mt-0.5 font-medium">{data.artist || "Spotify"}</p>
      </div>
    </a>
  );
}

// --- Main Compact Component ---

export default function CompactPortfolio({
  hero,
  experience,
  projects,
  skills,
  education,
  githubData,
  spotifyData,
  hireMe,
  contact,
}: any) {
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const { theme, setTheme } = useTheme();
  const safeHero = hero || {};
  const safeExp = experience || [];
  const safeProj = projects || [];
  const safeSkills = skills || [];
  const safeEdu = education || [];
  const safeHireMe = hireMe || [];

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  // Animation variants
  const containerVars = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };
  const itemVars = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
  };

  // Flatten skills into a single array for the compact skill cloud
  const flatSkills = safeSkills.flatMap((category: any) => category.skills || []);

  return (
    <div className="min-h-screen bg-transparent text-neutral-800 dark:text-neutral-300 font-sans selection:bg-neutral-300 dark:selection:bg-neutral-800 flex justify-center w-full pb-20 relative z-50">
      <FloatingParticles />
      {/* Hide global elements via CSS injection so layout is unbothered */}
      <style>{`
        .global-navbar-component { display: none !important; }
      `}</style>
      
      <div className="w-[100%] max-w-[640px] px-6 pt-12 md:pt-16">
        <motion.div variants={containerVars} initial="hidden" animate="visible" className="flex flex-col gap-6">
          
          {/* Nav */}
          <motion.nav variants={itemVars} className="flex items-center justify-between mb-4">
            <span className="text-neutral-900 dark:text-white font-bold tracking-tight text-lg">{safeHero.name?.split(' ')[0]?.toLowerCase() || 'sachin'}.</span>
            <div className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-xs font-semibold text-neutral-500">
              <a href="#experience" className="hover:text-neutral-800 dark:hover:text-neutral-200 transition-colors">experience</a>
              <a href="#projects" className="hover:text-neutral-800 dark:hover:text-neutral-200 transition-colors">projects</a>
              <a href="#skills" className="hover:text-neutral-800 dark:hover:text-neutral-200 transition-colors">skills</a>
              <button onClick={toggleTheme} className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-neutral-200 dark:bg-neutral-800/50 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors border border-transparent dark:border-neutral-700/50 shrink-0">
                {theme === 'dark' ? <Sun size={12} /> : <Moon size={12} />}
              </button>
            </div>
          </motion.nav>

          {/* Hero Card */}
          <motion.div variants={itemVars} className="border border-neutral-200 dark:border-neutral-800/60 rounded-[16px] p-6 sm:p-8 bg-white dark:bg-[#111111]/30 backdrop-blur-md shadow-sm dark:shadow-none">
            <div className="flex items-center justify-between mb-6">
              <AnimatedGreeting text={safeHero.greeting || "hi there, I'm"} />
              <div className="text-[13px] text-neutral-400 font-mono tracking-widest hidden sm:block">
                  {new Date().toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </div>
            </div>
            
            <div className="flex items-center gap-5 mb-5">
              {safeHero.profileImage && (
                <div className="w-16 h-16 rounded-full overflow-hidden border border-neutral-200 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-800 shrink-0 shadow-sm">
                  <Image src={safeHero.profileImage} alt={safeHero.name || "Profile"} width={64} height={64} className="object-cover w-full h-full contrast-[1.05] saturate-[1.2] brightness-[1.05] dark:brightness-[0.9] hover:saturate-[1.4] transition-all duration-500" />
                </div>
              )}
              <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">{safeHero.name}</h1>
            </div>
            
            <div className="flex items-center gap-3 text-[15px] text-neutral-500 mb-8 font-medium">
              <span>20, he/him</span>
              <span className="w-1.5 h-1.5 rounded-full bg-neutral-300 dark:bg-neutral-700"></span>
              <span>Full Stack Developer</span>
              <span className="w-1.5 h-1.5 rounded-full bg-neutral-300 dark:bg-neutral-700"></span>
              <span>from India.</span>
            </div>

            <div className="flex flex-wrap gap-3 mb-8">
              {safeHero.resumeUrl && (
                <a href={safeHero.resumeUrl} target="_blank" rel="noreferrer" className="px-5 py-2 bg-neutral-900 dark:bg-white text-white dark:text-black text-[13px] font-bold rounded-lg hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors shadow-sm">
                  Resume
                </a>
              )}
              {safeHero.socialLinks?.map((social: any, i: number) => {
                const Icon = social.name.toLowerCase() === 'github' ? Github : 
                            social.name.toLowerCase() === 'linkedin' ? Linkedin :
                            social.name.toLowerCase() === 'twitter' ? Twitter : Mail;
                return (
                  <a key={i} href={social.href} target="_blank" rel="noreferrer" className="w-[36px] h-[36px] border border-neutral-200 dark:border-neutral-800 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors flex items-center justify-center group shadow-sm dark:shadow-none">
                    <Icon size={16} className="text-neutral-600 dark:text-neutral-400 group-hover:text-black dark:group-hover:text-white transition-colors" />
                  </a>
                );
              })}
            </div>
            
            <p className="text-[16px] leading-relaxed text-neutral-600 dark:text-neutral-400 font-medium max-w-[95%]">
              {safeHero.description}
            </p>
          </motion.div>

          {/* Experience Section */}
          <motion.section variants={itemVars} id="experience">
            <div className="border border-neutral-200 dark:border-neutral-800/60 rounded-[16px] p-6 sm:p-8 bg-white dark:bg-[#111111]/30 shadow-sm dark:shadow-none">
              <h2 className="text-[17px] font-bold text-neutral-900 dark:text-neutral-100 mb-6 tracking-tight">Work Experience.</h2>
              <div className="flex flex-col gap-4">
                {safeExp.map((exp: any, i: number) => (
                  <div key={i} className="border border-neutral-200 dark:border-neutral-800/60 rounded-[16px] p-5 hover:bg-neutral-50 dark:hover:bg-[#1a1a1a]/40 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3.5">
                        <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-800/80 flex items-center justify-center border border-neutral-200 dark:border-neutral-700 shrink-0">
                          {exp.logo ? (
                            <Image src={exp.logo} alt={exp.company} width={24} height={24} className="rounded-full" />
                          ) : (
                            <span className="text-xs font-bold text-neutral-600 dark:text-neutral-300">{exp.company?.charAt(0)}</span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <h3 className="text-[16px] font-bold text-neutral-900 dark:text-neutral-100">{exp.company}</h3>
                          <p className="text-[13px] text-neutral-500 mt-0.5">{exp.role}</p>
                        </div>
                      </div>
                      <span className="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 px-2.5 py-1 bg-neutral-100 dark:bg-neutral-800/40 rounded-full border border-neutral-200 dark:border-neutral-800/50">{exp.period}</span>
                    </div>
                    <p className="text-[15px] text-neutral-600 dark:text-neutral-400 leading-relaxed mt-4">
                      {exp.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.section>

          {/* Projects / Selected Work Section */}
          <motion.section variants={itemVars} id="projects">
            <div className="border border-neutral-200 dark:border-neutral-800/60 rounded-[16px] p-6 sm:p-8 bg-white dark:bg-[#111111]/30 shadow-sm dark:shadow-none">
              <h2 className="text-[17px] font-bold text-neutral-900 dark:text-neutral-100 mb-6 tracking-tight">Projects.</h2>
              <div className="flex flex-col gap-4">
                {safeProj.filter((p:any) => p.featured || p.isVisibleInHomeProjects).slice(0, 4).map((proj: any, i: number) => (
                  <div 
                    key={i} 
                    onClick={() => setSelectedProject(proj)}
                    className="border border-neutral-200 dark:border-neutral-800/60 rounded-[16px] p-5 hover:bg-neutral-50 dark:hover:bg-[#1a1a1a]/40 transition-colors group cursor-pointer"
                  >
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-3">
                        {proj.logo && (
                            <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-1.5 flex items-center justify-center shrink-0">
                              <Image src={proj.logo} alt="logo" width={20} height={20} className="w-full h-full object-contain" />
                            </div>
                        )}
                        <h3 className="text-[16px] font-bold text-neutral-900 dark:text-neutral-100">{proj.title}</h3>
                      </div>
                      <div className="flex gap-3 text-[12px] font-medium shrink-0">
                        <div className="flex items-center gap-1 text-neutral-500 group-hover:text-primary transition-colors border border-neutral-200 dark:border-neutral-800 px-2.5 py-1 rounded-md bg-neutral-50 dark:bg-neutral-900/50">
                           details <ArrowRight size={10} className="-rotate-45" />
                        </div>
                      </div>
                    </div>
                    <div className="pl-0 mt-4">
                        <p className="text-[15px] text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4 line-clamp-3">
                          {proj.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {proj.tags?.slice(0,4).map((tag: string, j: number) => (
                            <span key={j} className="text-[11px] px-2.5 py-1 bg-neutral-100 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-800 rounded-md text-neutral-600 dark:text-neutral-400 font-medium">{tag}</span>
                          ))}
                        </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.section>

          {/* Skills Section */}
          <motion.section variants={itemVars} id="skills">
            <div className="border border-neutral-200 dark:border-neutral-800/60 rounded-[16px] p-6 sm:p-8 bg-white dark:bg-[#111111]/30 shadow-sm dark:shadow-none">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[17px] font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Skills.</h2>
                <span className="text-[10px] text-neutral-500 font-medium tracking-tight">hover on this 🤫</span>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {flatSkills.map((skill: any, j: number) => (
                  <span key={j} className="text-[12px] px-3 py-1.5 bg-neutral-100 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-800 rounded-md text-neutral-700 dark:text-neutral-300 font-medium flex items-center gap-1.5 hover:scale-105 transition-transform cursor-default">
                    {skill.name}
                  </span>
                ))}
              </div>
            </div>
          </motion.section>

          {/* Education Section */}
          <motion.section variants={itemVars} id="education">
            <div className="border border-neutral-200 dark:border-neutral-800/60 rounded-[16px] p-6 sm:p-8 bg-white dark:bg-[#111111]/30 shadow-sm dark:shadow-none">
              <h2 className="text-[17px] font-bold text-neutral-900 dark:text-neutral-100 mb-6 tracking-tight">Education.</h2>
              <div className="flex flex-col gap-4">
                {safeEdu.map((edu: any, i: number) => (
                  <div key={i} className="border border-neutral-200 dark:border-neutral-800/60 rounded-[12px] p-5 flex flex-col gap-1.5 hover:bg-neutral-50 dark:hover:bg-[#1a1a1a]/40 transition-colors">
                     <div className="flex justify-between items-center">
                        <h3 className="text-[16px] font-bold text-neutral-900 dark:text-neutral-100">{edu.school}</h3>
                        <span className="text-[13px] font-medium text-neutral-500 bg-neutral-100 dark:bg-neutral-800/40 px-2.5 py-1 rounded-md border border-neutral-200 dark:border-neutral-800/50 shrink-0">{edu.year}</span>
                     </div>
                     <p className="text-[15px] text-neutral-600 dark:text-neutral-400 font-medium">{edu.degree}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.section>

          {/* GitHub Graph */}
          <motion.section variants={itemVars} id="github" className="w-full overflow-hidden">
            <div className="border border-neutral-200 dark:border-neutral-800/60 rounded-[16px] p-6 sm:p-8 bg-white dark:bg-[#111111]/30 shadow-sm dark:shadow-none">
               <h2 className="text-[17px] font-bold text-neutral-900 dark:text-neutral-100 mb-6 tracking-tight">GitHub Contributions.</h2>
               <MinimalGitHubGraph initialData={githubData} />
            </div>
          </motion.section>

          {/* Hire Me Section */}
          <motion.section variants={itemVars} id="hire-me">
            <div className="border border-neutral-200 dark:border-neutral-800/60 rounded-[16px] p-6 sm:p-8 bg-white dark:bg-[#111111]/30 shadow-sm dark:shadow-none">
               <h2 className="text-[17px] font-bold text-neutral-900 dark:text-neutral-100 mb-6 tracking-tight">Available for Hire.</h2>
               
               <div className="relative h-[240px] w-full flex items-center justify-center mt-4">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-neutral-100 dark:bg-neutral-800/80 border border-neutral-200 dark:border-neutral-700/50 px-3 py-1.5 rounded-lg shadow-sm">
                    <span className="font-bold text-[10px] uppercase tracking-widest text-neutral-500">
                      Ideal fit as
                    </span>
                  </div>

                  {[
                    { text: "Design Engineer", hex: "#f97316", d: "M100 120 C 150 120, 250 20, 450 20" },
                    { text: "App Developer", hex: "#22c55e", d: "M100 120 C 150 120, 250 120, 450 120" },
                    { text: "Software Developer", hex: "#a855f7", d: "M100 120 C 150 120, 250 220, 450 220" }
                  ].map((item, i) => (
                    <AnimatedLane key={i} item={item} index={i} />
                  ))}
               </div>
            </div>
          </motion.section>

          {/* Contact (Let's work together) */}
          <motion.section variants={itemVars} id="contact">
            <div className="border border-neutral-200 dark:border-neutral-800/60 rounded-[16px] p-8 sm:p-10 bg-white dark:bg-[#111111]/30 flex flex-col items-center text-center shadow-sm dark:shadow-none">
              <h2 className="text-[24px] sm:text-[28px] font-bold text-neutral-900 dark:text-neutral-100 tracking-tight mb-4 tracking-tight">Let&apos;s work together.</h2>
              <p className="text-[13px] sm:text-[14px] text-neutral-600 dark:text-neutral-400 font-medium mb-8 max-w-[90%] md:max-w-[80%] leading-relaxed">
                  I&apos;m always interested in new opportunities and exciting projects. Whether you have a project in mind or just want to chat about tech, I&apos;d love to hear from you.
              </p>

              <a href={`mailto:${contact?.email || 'sachinsharmaaofficial@gmail.com'}`} className="flex items-center gap-2.5 px-6 py-3.5 bg-neutral-900 dark:bg-white text-white dark:text-black text-[14px] font-bold rounded-[10px] hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors shadow-sm mb-6">
                <Mail size={16} />
                Get in touch
              </a>

              <div className="flex items-center gap-4 mb-8">
                {safeHero.socialLinks?.map((social: any, i: number) => {
                  const Icon = social.name.toLowerCase() === 'github' ? Github : 
                              social.name.toLowerCase() === 'linkedin' ? Linkedin :
                              social.name.toLowerCase() === 'twitter' ? Twitter : null;
                  if (!Icon) return null;
                  return (
                    <a key={i} href={social.href} target="_blank" rel="noreferrer" className="w-[42px] h-[42px] rounded-xl border border-neutral-200 dark:border-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-400 hover:text-black dark:hover:text-white transition-colors bg-neutral-50 dark:bg-[#1a1a1a]/50">
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
              
              <div className="flex flex-col gap-2 items-center text-[12px] text-neutral-500 font-medium">
                <p>Currently available for freelance work and full-time opportunities</p>
                <p>Response time: Usually within 24 hours</p>
              </div>
            </div>
          </motion.section>

          {/* Spotify Addition */}
          <motion.div variants={itemVars}>
             <MinimalSpotify initialData={spotifyData} />
          </motion.div>

          <motion.div variants={itemVars} className="pb-10 pt-2 flex flex-col gap-4">
            <div className="border border-neutral-200 dark:border-neutral-800/60 rounded-[16px] py-4 bg-white dark:bg-[#111111]/30 flex items-center justify-center shadow-sm dark:shadow-none text-center">
               <span className="text-[13px] font-medium text-neutral-500 italic">Never forget, why you started ❤️</span>
            </div>
            
            <div className="flex justify-center gap-4">
               <a href="/rss.xml" className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest border border-emerald-500/20 px-3 py-1.5 rounded-lg bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors">
                 RSS Feed
               </a>
               <a href="/sitemap.xml" className="text-[11px] font-bold text-neutral-500 uppercase tracking-widest border border-neutral-200 dark:border-neutral-800 px-3 py-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                 Sitemap
               </a>
            </div>

            <div className="text-center text-[12px] text-neutral-500 font-medium pt-2 flex items-center justify-center gap-1.5">
              <span>Founder of</span>
              <a
                href="https://mojostudio.in"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline hover:text-foreground font-semibold transition-colors duration-300"
              >
                mojostudio.in
              </a>
              <span>,</span>
              <a
                href="https://mojodocs.in"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline hover:text-foreground font-semibold transition-colors duration-300"
              >
                mojodocs.in
              </a>
              <span>&</span>
              <a
                href="https://vanisagar.in"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline hover:text-foreground font-semibold transition-colors duration-300"
              >
                vanisagar.in
              </a>
            </div>
          </motion.div>

        </motion.div>
      </div>

      <AnimatePresence>
        {selectedProject && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProject(null)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto no-scrollbar rounded-3xl"
            >
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 z-[210] p-2 rounded-full bg-black/50 text-white hover:bg-black/80 transition-colors backdrop-blur-md border border-white/10"
              >
                <X size={20} />
              </button>
              <div className="p-1">
                <ProjectCard {...selectedProject} />
              </div>
            </motion.div>s
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
